import { Platform } from '../../platform';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import {
  buttonKeybindTextVariants,
  buttonKeybindVariants,
} from '../Button/Button.variants';
import { Checkbox } from '../Checkbox';
import { Radio } from '../Radio';
import { Toggle } from '../Toggle';
import type { MenuListItemControl } from './MenuListItem.types';
import type { MenuListItemVariantProps } from './MenuListItem.variants';

const IS_WEB = Platform.OS === 'web';

type Size = NonNullable<MenuListItemVariantProps['size']>;
type Variant = NonNullable<MenuListItemVariantProps['variant']>;

/**
 * Trailing right-slot controls for {@link MenuListItem} — Figma "Flashka
 * Design System" file `YZK9r3wDhvw1pY60KlVEIj`, frame `4036:30431`:
 *
 *   - keybinding — the keyboard-shortcut keycap (identical to Button's chip;
 *     WEB-ONLY, same as Button — mobile has no keyboard and doesn't bundle the
 *     Chivo Mono face).
 *   - radio / checkbox — the DS {@link Radio} / {@link Checkbox} in
 *     presentational mode (`interactive={false}`): the ROW owns the press and
 *     the checkable a11y semantics; the control is a pure indicator. `checked`
 *     also drives the whole row to its `selected` variant (MenuListItem.tsx).
 *   - toggle — the DS {@link Toggle} in presentational mode. Unlike
 *     radio/checkbox it does NOT flip the row to `selected` — the switch
 *     communicates its state itself (iOS-settings style).
 */

/** Row band → DS Radio/Checkbox size (16/20/24 px scale). */
const CONTROL_SIZE_FOR: Record<Size, 's' | 'm' | 'l'> = {
  xl: 'l',
  l: 'm',
  m: 'm',
  s: 's',
};

/**
 * Row band → DS Toggle size (xs 24 · s 28 · m 32 px tall).
 *
 * A toggle is the widest control a row can carry (the track is 2× its height),
 * so matching it to the band the way radio/checkbox do makes it dominate the
 * row — a 28px switch reads as the subject of a 44px row rather than its
 * trailing state. Every band therefore sits one step below its radio/checkbox
 * counterpart, bottoming out at `xs`.
 */
const TOGGLE_SIZE_FOR: Record<Size, 'xs' | 's' | 'm'> = {
  xl: 's',
  l: 'xs',
  m: 'xs',
  s: 'xs',
};

/** Row variant → Button keybind-chip palette. `error` borrows the chip's
 *  `alert` red; primary / secondary map 1:1. */
const CHIP_VARIANT_FOR: Record<Variant, 'primary' | 'secondary' | 'alert'> = {
  secondary: 'secondary',
  primary: 'primary',
  error: 'alert',
};

/**
 * Render the trailing control for a row's right slot. Returns `null` when
 * there's nothing to draw (a keybinding on native — no keyboard there).
 */
export function renderMenuControl(
  control: MenuListItemControl,
  size: Size,
  variant: Variant,
): React.ReactNode {
  if (control.type === 'keybinding') {
    // Keyboard shortcuts are a web affordance — mirror Button's web-only chip.
    if (!IS_WEB) return null;
    const chipSize = size === 's' ? ('sm' as const) : ('md' as const);
    const chipVariant = CHIP_VARIANT_FOR[variant];
    return (
      <View className={buttonKeybindVariants({ variant: chipVariant, chipSize })}>
        <Text className={buttonKeybindTextVariants({ variant: chipVariant, chipSize })}>
          {control.keys}
        </Text>
      </View>
    );
  }

  if (control.type === 'radio') {
    return (
      <Radio
        checked={control.checked}
        size={CONTROL_SIZE_FOR[size]}
        interactive={false}
      />
    );
  }

  if (control.type === 'toggle') {
    return (
      <Toggle
        checked={control.checked}
        size={TOGGLE_SIZE_FOR[size]}
        interactive={false}
      />
    );
  }

  // checkbox — `primary` = brand purple fill + white check, matching the
  // selected-row tint (Figma 4036:30431).
  return (
    <Checkbox
      checked={control.checked}
      variant="primary"
      size={CONTROL_SIZE_FOR[size]}
      interactive={false}
    />
  );
}
